import { useCallback, useState } from 'react';

/**
 * A `useState` that survives a reload. For view preferences only — anything the
 * server owns belongs in a query, not here.
 *
 * Reads are lazy and guarded: a private-mode browser or a value written by an
 * older build shouldn't take the page down, so a bad read falls back to
 * `initial` rather than throwing during render.
 */
export function useLocalStorage<T>(key: string, initial: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Quota or private mode — the preference just won't persist.
      }
    },
    [key],
  );

  return [value, set];
}
